import { fetchUserAttributes, fetchAuthSession } from '@aws-amplify/auth';
import { resolveSessionForType } from './resolveSession';

export async function readAuthorSetupIdentity() {
  const session = await resolveSessionForType('author', {
    restore: true,
    read: async () => ({ attributes: await fetchUserAttributes(), session: await fetchAuthSession() }),
  });
  const { attributes, session: auth } = session.data;
  const token = auth.tokens?.idToken?.toString();
  const userSub = attributes.sub;
  const firstName = attributes['custom:firstname'] || attributes.given_name;
  const lastName = attributes['custom:lastname'] || attributes.family_name;
  if (!userSub || !firstName || !lastName || !token) throw new Error('Please return to Author Login, then try again.');
  return { userSub, firstName, lastName, email: attributes.email || '', middleInitial: attributes['custom:middleinitial'] || '', token };
}
export async function setupRequest(url, identity, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + identity.token },
    body: JSON.stringify(body),
  });
  let data;
  try { data = await response.json(); } catch { throw new Error('We couldn’t read the response. Please try again.'); }
  if (!response.ok) {
    const details = { status: response.status, code: typeof data?.code === 'string' ? data.code : undefined };
    if (details.code === 'CHECKOUT_REVIEW_REQUIRED') throw Object.assign(new Error('Your earlier membership checkout needs review. Contact Lady Liza before opening another checkout.'), details);
    if (details.code === 'CONNECT_REVIEW_REQUIRED') throw Object.assign(new Error('Your earlier Stripe setup needs review. Contact Lady Liza before starting another connection.'), details);
    if (details.code === 'ACCOUNT_BUSY') throw Object.assign(new Error('An account update is in progress. Wait a moment, then retry Stripe setup.'), details);
    if (details.code === 'PROFILE_UNAVAILABLE') throw Object.assign(new Error('Your saved Author profile needs attention. Contact Lady Liza before continuing.'), details);
    if (response.status === 409) throw Object.assign(new Error('This choice is unavailable or an update is already in progress. Please check your details and try again.'), details);
    throw Object.assign(new Error('We couldn’t complete that step. Please try again. If your session has ended, return to Author Login.'), details);
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('We couldn’t read the response. Please try again.');
  return data;
}
export function stripeDestination(value) {
  const url = new URL(value);
  if (url.protocol !== 'https:' || !(url.hostname === 'stripe.com' || url.hostname.endsWith('.stripe.com')) || url.username || url.password || url.port) throw new Error('We couldn’t open Stripe. Please try again.');
  return url.href;
}
export function normalizeSetupStripe(raw) {
  const data = raw?.stripe || raw;
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Payment status is unavailable.');
  const accountId = data.connectedAccountId || data.accountId || data.stripeAccountId || data.id || null;
  const bool = (a, b) => typeof a === 'boolean' ? a : typeof b === 'boolean' ? b : null;
  const charges = bool(data.charges_enabled, data.chargesEnabled);
  const payouts = bool(data.payouts_enabled, data.payoutsEnabled);
  const details = bool(data.details_submitted, data.detailsSubmitted);
  const due = data.requirements_due === true || (Array.isArray(data.requirements?.currently_due) && data.requirements.currently_due.length > 0);
  const recognized = ['connectedAccountId', 'accountId', 'stripeAccountId', 'id', 'charges_enabled', 'chargesEnabled', 'payouts_enabled', 'payoutsEnabled', 'connected'].some(key => Object.prototype.hasOwnProperty.call(data, key));
  if (!recognized) throw new Error('Payment status is unavailable.');
  const pending = data.setupPending === true;
  return { accountId, ready: Boolean(accountId && charges === true && payouts === true && details === true && !due && !pending), due, pending, unknown: Boolean(accountId && (charges === null || payouts === null || details === null)) };
}
